/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomas Burian (burian@cadwork.cz)
 *
 */
import {Canvas, RenderDocument} from "js_renderer";
import IScene from "../../scene_interface";
import IDocument from "../../structural_elements/document_interface";

export default
class Fog {
    private _sceneAPI: IScene;
    private _canvas: Canvas;
    private _sceneDocument: IDocument;
    private _fogGradientImageData: ImageData;
    private _enabled: boolean;
    private _density: number;
    private _distance: number;
    private _color: number[];
    private _renderDocument: RenderDocument;

    constructor(aRenderDocument: RenderDocument) {
        this._renderDocument = aRenderDocument;
        this._sceneAPI = aRenderDocument.getSceneAPI();
        this._canvas = this._sceneAPI.getCanvas();
        this._sceneDocument = this._sceneAPI.getDocument();

        this._fogGradientImageData = null;

        let imageFog = new Image();
        imageFog.onload = () => {
            let canvas = document.createElement('canvas');
            canvas.width = imageFog.width;
            canvas.height = imageFog.height;

            let context = canvas.getContext('2d');
            context.drawImage(imageFog, 0, 0);

            this._fogGradientImageData = context.getImageData(0, 0, canvas.width, canvas.height);
        };
        imageFog.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAABAAAAAABCAYAAABADtw1AAAABmJLR0QA/wD/AP+gvaeTAAAACXBIWXMAAA3XAAAN1wFCKJt4AAAAB3RJTUUH4QcSEAIMflqsWgAAAxBJREFUWMOFV1l2IzEIrJIcu3Ofyf1vFuajQRRIPZP3ksgSO8Vimpn9/PmBwUBg/Y2fuKNS8KaIWwCgORcJGgCnJ+IcbybSAdK1mMsnALOluWih6FH7nA1G2H3wS6cMZiD9oNPAVFVlCd1qFtKQvCMiCDdHyksmEUKXvFRbiYdZ9Xm3P+Lm4hhi/XUFZSUlrCq+U2njfYWEK4M1Y2rT009HUj2BlBTvdOmP0mbi2bQVbAQ4e0zLVeY9xFKurfGsEAGgPcQDqHWABcqi4/S/RjvwIEFucN2U01YNpV6pIwV1BPfgQGA9/Mh4MCMdofOgmPCl2kNBQWpf/VhMidEt5/Q363HbqgKH7nCOtDwZNvdWr4JUjgJBOp9glsWS0gZ4YwewG6PuzG5h2k4BjrVSlbZ5LMqFpApebZaO5ZbX7kHrx5tfBUL0lGU8IDlF5BQtjh4bK3ZJdFsB3x8dT3RPFdOszWGru8WP2n0p067grwZbS4jeiIeOARBkshF6ljfph0FDCTtJ+cw1X1QeCAwTGaIDAAa7jtBvSz4X3lweVAad1u3xm9d74n1d+FwfvL8/+FwXPt8ffF0Xvl4TY06M18QYAzM+j4H5GhhzYk6/m2Od5xx55zzAgBkdAwO/GOtsIAzDf+8znDbvR5vblNbIG9GWwTShM+/3vwIqW9GPft34SpVw6cndRM/eM6LfgVmvpW6bZFatih+s+8inrd2ItFID1N4qec5yuIFe8Mrad9cc/c82UMqS53WCnYfP7zpwKS9Jmxy5r8pcw+MIb7spasdb86/Npn9Mc92/dsHVfrS8lve1FGVuTsGn8K4eWBJb2zKkf1gd2WV3ZRutbLtX2UUO+dbdiCe6g5KyC+peJrl9xBF5xg7rjCzfNZg7dSs+31Wfwc5SD2x7ZcaGT7tjryzZ+9hHMlk6ztJe8sfybrK7MfY6PuD/eM+6/3W7pSaO4hpguKGfde/WJVQaAq0At2aRHU+eN20Cp7oTv0wzwdN+xerPbuJW67XvqP/53RU+lwXkZ3y0ZXFzrdfGoefF/v4XgrrB7sudohYAAAAASUVORK5CYII=";

        this._enabled = false;
        this._density = 6.0;
        this._distance = 80000.0;
        this._color = [0.0, 0.0, 1.0];
    }

    public readColor(aImgData: ImageData, aAngle: number): number[] {
        if (aImgData) {
            let val = (Math.sin(aAngle) + 1.0) / 2.0;

            let x = Math.round(aImgData.width * val) - 1;
            if (x < 0) {
                x = 0;
            }
            let y = 0;

            // Now you can access pixel data from imageData.data.
            // It's a one-dimensional array of RGBA values.
            // Here's an example of how to get a pixel's color at (x,y)
            let index = (y * aImgData.width + x) * 4;
            let red = aImgData.data[index];
            let green = aImgData.data[index + 1];
            let blue = aImgData.data[index + 2];
            let alpha = aImgData.data[index + 3];

            return [red / 255, green / 255, blue / 255];
        } else {
            return [-1, -1, -1];
        }
    }

    public getColorFromGradient(aAngleRad: number) {
        // this._sceneAPI.lightingModule.currentLighting.getSunLightElevation()
        return this.readColor(this._fogGradientImageData, aAngleRad);
    }

    public isEnabled(): boolean {
        return this._enabled;
    }

    public getColor(): number[] {
        return this._color;
    }

    public getDensity(): number {
        return this._density;
    }

    public getDistance(): number {
        return this._distance;
    }

    public setEnabled(aValue: boolean) {
        if (this._enabled !== aValue) {
            this._enabled = aValue;

            // init fog in shaders
            let webglRenderGraph = this._renderDocument.getRenderer().getWebGLRenderGraph();
            if (webglRenderGraph) {
                webglRenderGraph.initialize();
            }
        }

        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public setColor(aValue: number[]) {
        this._color = aValue;
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public setDensity(aValue: number) {
        this._density = aValue;
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public setDistance(aValue: number) {
        this._distance = aValue;
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }
}
